import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, ExternalLink, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import AllFieldsSection from '../../components/AllFieldsSection';
import EntityAuditList from '../../components/EntityAuditList';
import FieldList from '../../components/FieldList';
import { formatDate } from '../../lib/format';
import {
  LOCATION_TYPES,
  PLACE_FIELD_LABELS,
  locationTypeLabel,
  locationTypeShort,
  originHelp,
  originLabel,
  originVariant,
} from '../../../constants/votingLocations';
import type { ElectionCycle } from './CyclesView';

interface Availability {
  id: string;
  election_calendar_id: string;
  election_name: string | null;
  election_year: number;
  election_type: string | null;
  location_type: string;
  label: string | null;
  directions: string | null;
  start_date: string | null;
  end_date: string | null;
  hours_text: string | null;
  hours_by_date: Record<string, { open: string; close: string | null }>;
  origin: 'import' | 'projected' | 'manual';
}

interface VotingPlace {
  id: string;
  match_key: string;
  display_name: string;
  display_name_source: 'file' | 'address' | 'admin';
  county_name: string;
  county_district_id: string | null;
  address_line1: string;
  address_line2: string | null;
  city: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
  manual_fields: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
  availability: Availability[];
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'activity', label: 'Activity' },
] as const;

const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

const EMPTY_AVAILABILITY = {
  id: '',
  election_calendar_id: '',
  location_type: 'drop_box',
  label: '',
  directions: '',
  start_date: '',
  end_date: '',
  hours_text: '',
};

/**
 * Voting place detail (PRD §5.3).
 *
 * The identity fields carry provenance markers: a field an admin has edited is
 * protected from the workbook import and says so, with a way to hand it back.
 * That visibility is the point — silent protection is as confusing as silent
 * overwriting, just in the other direction.
 */
const VotingPlaceDetailPage: React.FC = () => {
  const { placeId } = useParams<{ placeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]['key']>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [availabilityForm, setAvailabilityForm] = useState(EMPTY_AVAILABILITY);
  const [confirmDelete, setConfirmDelete] = useState<Availability | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const place = useQuery({
    queryKey: ['admin-voting-locations', 'place', placeId],
    queryFn: async () => {
      const response = await api.get(`/admin/voting-locations/${placeId}`);
      return response.data.data as VotingPlace;
    },
    enabled: Boolean(placeId),
  });

  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles'],
    queryFn: async () => {
      const response = await api.get('/election-calendars');
      return (response.data.data ?? []) as ElectionCycle[];
    },
    enabled: availabilityOpen,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-voting-locations'] });
    queryClient.invalidateQueries({ queryKey: ['admin-list', 'voting-locations'] });
  };

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.put(`/admin/voting-locations/${placeId}`, payload),
    onSuccess: () => {
      toast.success('Saved — this stays through future imports');
      setEditOpen(false);
      refresh();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save')),
  });

  const revertMutation = useMutation({
    mutationFn: (field: string) =>
      api.post(`/admin/voting-locations/${placeId}/manual-fields/revert`, { field }),
    onSuccess: () => {
      toast.success('Field handed back to the import');
      refresh();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to revert')),
  });

  const activeMutation = useMutation({
    mutationFn: (active: boolean) =>
      api.put(`/admin/voting-locations/${placeId}/${active ? 'activate' : 'deactivate'}`),
    onSuccess: (_response, active) => {
      toast.success(active ? 'Place reactivated' : 'Place deactivated — citizens no longer see it');
      setConfirmDeactivate(false);
      refresh();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to change the status')),
  });

  const saveAvailability = useMutation({
    mutationFn: () => {
      const payload = {
        election_calendar_id: availabilityForm.election_calendar_id,
        location_type: availabilityForm.location_type,
        label: availabilityForm.label.trim() || null,
        directions: availabilityForm.directions.trim() || null,
        start_date: availabilityForm.start_date || null,
        end_date: availabilityForm.end_date || null,
        hours_text: availabilityForm.hours_text.trim() || null,
      };
      return availabilityForm.id
        ? api.put(`/admin/voting-locations/${placeId}/availability/${availabilityForm.id}`, payload)
        : api.post(`/admin/voting-locations/${placeId}/availability`, payload);
    },
    onSuccess: () => {
      toast.success(availabilityForm.id ? 'Location updated — it now survives imports' : 'Location added');
      setAvailabilityOpen(false);
      refresh();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save the location')),
  });

  const deleteAvailability = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/voting-locations/${placeId}/availability/${id}`),
    onSuccess: () => {
      toast.success('Location removed');
      setConfirmDelete(null);
      refresh();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to remove the location')),
  });

  if (place.isLoading) {
    return <AdminPageShell title="Voting place"><p className="text-sm">Loading…</p></AdminPageShell>;
  }
  if (place.isError || !place.data) {
    return (
      <AdminPageShell title="Voting place">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm">Couldn't load this voting place.</p>
          <Button variant="outline" size="sm" onClick={() => place.refetch()}>Retry</Button>
        </div>
      </AdminPageShell>
    );
  }

  const data = place.data;
  const protectedFields = new Set(data.manual_fields ?? []);

  /** A field's value, with its protection state and a way to give it back. */
  const fieldValue = (field: string, value: React.ReactNode) => {
    if (!protectedFields.has(field)) return value;
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span>{value}</span>
        <Chip label="Admin-edited" variant="success" size="sm" />
        <button
          type="button"
          onClick={() => revertMutation.mutate(field)}
          className="inline-flex items-center gap-0.5 text-xs text-text-tertiary underline hover:text-text-secondary dark:text-text-dark-tertiary"
          title="Stop protecting this field — the next import will set it from the state's file again"
        >
          <RotateCcw className="h-3 w-3" /> revert
        </button>
      </span>
    );
  };

  const openEdit = () => {
    setEditForm({
      display_name: data.display_name,
      address_line1: data.address_line1,
      address_line2: data.address_line2 ?? '',
      city: data.city ?? '',
      zip: data.zip ?? '',
      county_name: data.county_name,
      latitude: data.latitude == null ? '' : String(data.latitude),
      longitude: data.longitude == null ? '' : String(data.longitude),
    });
    setEditOpen(true);
  };

  const openAvailability = (existing?: Availability) => {
    setAvailabilityForm(
      existing
        ? {
            id: existing.id,
            election_calendar_id: existing.election_calendar_id,
            location_type: existing.location_type,
            label: existing.label ?? '',
            directions: existing.directions ?? '',
            start_date: existing.start_date ?? '',
            end_date: existing.end_date ?? '',
            hours_text: existing.hours_text ?? '',
          }
        : EMPTY_AVAILABILITY
    );
    setAvailabilityOpen(true);
  };

  const byCycle = data.availability.reduce<Record<string, Availability[]>>((acc, row) => {
    const key = row.election_name || `${row.election_year} ${row.election_type ?? ''}`.trim();
    (acc[key] ||= []).push(row);
    return acc;
  }, {});

  return (
    <AdminPageShell
      title={data.display_name}
      description={`${data.address_line1}${data.city ? `, ${data.city}` : ''} · ${data.county_name} County`}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin-next/elections?tab=locations')}>
            Back to list
          </Button>
          {data.active ? (
            <Button variant="outline" size="sm" onClick={() => setConfirmDeactivate(true)}>
              Deactivate
            </Button>
          ) : (
            <Button variant="primary" size="sm" onClick={() => activeMutation.mutate(true)}>
              Reactivate
            </Button>
          )}
        </>
      }
    >
      {!data.active && (
        <div className="card mb-3 flex items-center gap-3 border-l-4 border-warning-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            This place is deactivated. Citizens don't see it or any of its locations, and importing
            the state's file again won't turn it back on.
          </p>
        </div>
      )}

      <AdminTabs tabs={[...TABS]} activeKey={activeTab} onChange={(key) => setActiveTab(key as typeof activeTab)} />

      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Identity</h3>
              <Button variant="outline" size="sm" icon={Pencil} onClick={openEdit}>Edit</Button>
            </div>
            <FieldList
              fields={[
                { label: PLACE_FIELD_LABELS.display_name, value: fieldValue('display_name', data.display_name) },
                { label: PLACE_FIELD_LABELS.address_line1, value: fieldValue('address_line1', data.address_line1) },
                { label: PLACE_FIELD_LABELS.address_line2, value: fieldValue('address_line2', data.address_line2 || '—') },
                { label: PLACE_FIELD_LABELS.city, value: fieldValue('city', data.city || '—') },
                { label: PLACE_FIELD_LABELS.zip, value: fieldValue('zip', data.zip || '—') },
                { label: PLACE_FIELD_LABELS.county_name, value: fieldValue('county_name', data.county_name) },
                {
                  label: 'County district',
                  value: data.county_district_id
                    ? fieldValue('county_district_id', 'Mapped')
                    : <Chip label="Unmapped" variant="warning" size="sm" />,
                },
                {
                  label: 'Coordinates',
                  value: data.latitude != null && data.longitude != null
                    ? `${data.latitude}, ${data.longitude}`
                    : '— (geocoding not enabled yet)',
                },
                { label: 'Name source', value: data.display_name_source },
                { label: 'Identity key', value: data.match_key, copyValue: data.match_key },
                { label: 'Updated', value: formatDate(data.updated_at) },
              ]}
            />
            <div className="mt-3">
              <AllFieldsSection record={{ ...data, availability: `${data.availability.length} rows` }} />
            </div>
          </div>

          <div className="card p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                  What this place does
                </h3>
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  One row per role per election. Editing a row from the state's file keeps it through
                  future imports.
                </p>
              </div>
              <Button variant="outline" size="sm" icon={Plus} onClick={() => openAvailability()}>
                Add location
              </Button>
            </div>

            {data.availability.length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                Nothing scheduled here yet.
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(byCycle).map(([cycleName, rows]) => (
                  <div key={cycleName}>
                    <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                      {cycleName}
                    </h4>
                    <ul className="divide-y divide-border-light dark:divide-border-dark">
                      {rows.map((row) => (
                        <li key={row.id} className="flex items-start gap-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                                {locationTypeLabel(row.location_type)}
                              </span>
                              <span title={originHelp(row.origin)}>
                                <Chip label={originLabel(row.origin)} variant={originVariant(row.origin)} size="sm" />
                              </span>
                            </div>
                            {row.label && (
                              <div className="text-xs text-text-secondary dark:text-text-dark-secondary">{row.label}</div>
                            )}
                            <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                              {row.start_date || row.end_date
                                ? `${row.start_date ? formatDate(row.start_date) : '?'} – ${row.end_date ? formatDate(row.end_date) : '?'}`
                                : 'No dates set'}
                              {row.hours_text ? ` · ${row.hours_text}` : ''}
                              {Object.keys(row.hours_by_date || {}).length > 0
                                ? ` · ${Object.keys(row.hours_by_date).length} dated hours`
                                : ''}
                            </div>
                            {row.directions && (
                              <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                                {row.directions}
                              </div>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <Button variant="ghost" size="sm" icon={Pencil} onClick={() => openAvailability(row)}>
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" icon={Trash2} onClick={() => setConfirmDelete(row)}>
                              Remove
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <a
            href="/admin/data-import"
            className="inline-flex items-center gap-1 text-xs text-text-tertiary underline hover:text-text-secondary dark:text-text-dark-tertiary"
          >
            Locations are loaded in bulk from the Data Import tool <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {activeTab === 'activity' && <EntityAuditList entityType="voting_place" entityId={placeId!} />}

      <AdminModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit voting place"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={updateMutation.isLoading}
              onClick={() =>
                updateMutation.mutate({
                  ...editForm,
                  latitude: editForm.latitude === '' ? null : Number(editForm.latitude),
                  longitude: editForm.longitude === '' ? null : Number(editForm.longitude),
                })
              }
            >
              {updateMutation.isLoading ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Anything you change here is kept through future imports until you revert it. Changing the
            address also changes how this place is matched when the next file is imported.
          </p>
          {(['display_name', 'address_line1', 'address_line2', 'city', 'zip', 'county_name', 'latitude', 'longitude'] as const).map(
            (field) => (
              <div key={field}>
                <label className={labelClasses} htmlFor={`edit-${field}`}>
                  {PLACE_FIELD_LABELS[field] ?? field}
                </label>
                <input
                  id={`edit-${field}`}
                  className={inputClasses}
                  value={editForm[field] ?? ''}
                  onChange={(event) => setEditForm({ ...editForm, [field]: event.target.value })}
                />
              </div>
            )
          )}
        </div>
      </AdminModal>

      <AdminModal
        isOpen={availabilityOpen}
        onClose={() => setAvailabilityOpen(false)}
        title={availabilityForm.id ? 'Edit location' : 'Add location'}
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setAvailabilityOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!availabilityForm.election_calendar_id || saveAvailability.isLoading}
              onClick={() => saveAvailability.mutate()}
            >
              {saveAvailability.isLoading ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Saving marks this as admin-maintained, so the next state import won't replace it.
          </p>
          <div>
            <label className={labelClasses} htmlFor="availability-cycle">Election</label>
            <select
              id="availability-cycle"
              className={inputClasses}
              value={availabilityForm.election_calendar_id}
              onChange={(event) =>
                setAvailabilityForm({ ...availabilityForm, election_calendar_id: event.target.value })
              }
              disabled={Boolean(availabilityForm.id)}
            >
              <option value="">Select an election…</option>
              {[...(cycles.data ?? [])]
                .sort((a, b) => b.election_year - a.election_year)
                .map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className={labelClasses} htmlFor="availability-type">Type</label>
            <select
              id="availability-type"
              className={inputClasses}
              value={availabilityForm.location_type}
              onChange={(event) => setAvailabilityForm({ ...availabilityForm, location_type: event.target.value })}
            >
              {LOCATION_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClasses} htmlFor="availability-label">
              Name for this role (optional)
            </label>
            <input
              id="availability-label"
              className={inputClasses}
              placeholder="e.g. Curbside Drive-Up Ballot Drop-off"
              value={availabilityForm.label}
              onChange={(event) => setAvailabilityForm({ ...availabilityForm, label: event.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses} htmlFor="availability-start">Opens</label>
              <input
                id="availability-start" type="date" className={inputClasses}
                value={availabilityForm.start_date}
                onChange={(event) => setAvailabilityForm({ ...availabilityForm, start_date: event.target.value })}
              />
            </div>
            <div>
              <label className={labelClasses} htmlFor="availability-end">Closes</label>
              <input
                id="availability-end" type="date" className={inputClasses}
                value={availabilityForm.end_date}
                onChange={(event) => setAvailabilityForm({ ...availabilityForm, end_date: event.target.value })}
              />
            </div>
          </div>
          <div>
            <label className={labelClasses} htmlFor="availability-hours">Hours</label>
            <input
              id="availability-hours"
              className={inputClasses}
              placeholder="e.g. 7:00AM-7:00PM Election Day"
              value={availabilityForm.hours_text}
              onChange={(event) => setAvailabilityForm({ ...availabilityForm, hours_text: event.target.value })}
            />
          </div>
          <div>
            <label className={labelClasses} htmlFor="availability-directions">Directions (optional)</label>
            <input
              id="availability-directions"
              className={inputClasses}
              placeholder="e.g. east side of the parking lot"
              value={availabilityForm.directions}
              onChange={(event) => setAvailabilityForm({ ...availabilityForm, directions: event.target.value })}
            />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={Boolean(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteAvailability.mutate(confirmDelete.id)}
        title="Remove this location?"
        message={
          confirmDelete
            ? `${locationTypeShort(confirmDelete.location_type)} at ${data.display_name}. If it came from the state's file it will come back on the next import.`
            : ''
        }
        confirmText="Remove"
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={confirmDeactivate}
        onCancel={() => setConfirmDeactivate(false)}
        onConfirm={() => activeMutation.mutate(false)}
        title="Deactivate this place?"
        message={`Citizens will stop seeing ${data.display_name} and all ${data.availability.length} of its locations. Nothing is deleted, and re-importing the state's file won't turn it back on.`}
        confirmText="Deactivate"
        variant="danger"
      />
    </AdminPageShell>
  );
};

export default VotingPlaceDetailPage;
